// EditBid.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Chip,
  useTheme,
  alpha,
  IconButton
  // Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { Bid, CategoryItem, StatusItem, TypeItem, ApiResponse } from './types';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
// import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventIcon from '@mui/icons-material/Event';
import TitleIcon from '@mui/icons-material/Title';
import NotesIcon from '@mui/icons-material/Notes';
import DescriptionIcon from '@mui/icons-material/Description';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CategoryIcon from '@mui/icons-material/Category';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

interface EditBidProps {
  bid: Bid | null;
  categories: CategoryItem[];
  statuses: StatusItem[];
  bidTypes: TypeItem[];
  categoryMap: { [key: number]: string };
  statusMap: { [key: number]: string };
  typeMap: { [key: number]: string };
  onCancel: () => void;
  onSuccess: () => void;
}

const EditBid: React.FC<EditBidProps> = ({
  bid,
  categories,
  statuses,
  bidTypes,
  typeMap,
  onCancel,
  onSuccess
}) => {
  const theme = useTheme();

  const [editFormData, setEditFormData] = useState<Bid>(
    bid || {
      bidId: '',
      title: '',
      adminNote: '',
      description: '',
      category: [],
      status: '',
      goLiveDate: '',
      bidDeadline: '',
      awardDate: null,
      effectiveDate: null,
      departmentID: 0,
      statusID: 0,
      categoryIDs: []
    }
  );
  const [loading, setLoading] = useState(false);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);

  // Get status color based on status name
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
      case 'active':
        return theme.palette.success.main;
      case 'closed':
      case 'inactive':
        return theme.palette.error.main;
      case 'pending':
      case 'in review':
        return theme.palette.warning.main;
      case 'awarded':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const updateBid = async () => {
    setLoading(true);
    try {
      const matchingCategoryIDs = categories
        .filter((cat) => editFormData.category.includes(cat.CategoryName))
        .map((cat) => cat.CategoryID);

      // Use BidID if available, otherwise try to parse from bidId string
      const bidId =
        editFormData.BidID ||
        (editFormData.bidId
          ? parseInt(editFormData.bidId.replace(/\D/g, ''))
          : 0);

      const payload = {
        Title: editFormData.title,
        ContractNo: editFormData.ContractNo,
        CategoryIDs: matchingCategoryIDs,
        DepartmentID: editFormData.departmentID || 1, // Default value if not provided
        // GoLiveDate: editFormData.goLiveDate || new Date().toISOString(),
        EffectiveDate: editFormData.effectiveDate || null,
        StatusID: editFormData.statusID ? editFormData.statusID : 1, // Default value if not found
        Description: editFormData.description,
        AdminNote: editFormData.adminNote,
        SendNotifyProcess: 'Update and Notify'
        // AwardDate: editFormData.awardDate || null
      };

      const response = (await httpRequest(
        'PATCH',
        `/bid_portal/bid_details/update/${bidId}`,
        payload
      )) as ApiResponse<any>;

      if (response.status === 'success') {
        toast.success('Bid updated successfully');
        onSuccess();
      } else {
        toast.error('Failed to update bid: ' + response.message);
      }
    } catch (error) {
      console.error('Error updating bid:', error);
      toast.error('An error occurred while updating the bid');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateBid();
  };

  if (!bid) {
    return (
      <Card sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No bid selected for editing
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onCancel}
          sx={{ mt: 2, borderRadius: 1 }}
        >
          Return to Bid List
        </Button>
      </Card>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={onCancel}
          sx={{
            mr: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main
          }}
        >
          Edit Bid #{editFormData.bidId || editFormData.BidID}
        </Typography>
        <Chip
          label={editFormData.status}
          size="small"
          sx={{
            ml: 2,
            bgcolor: alpha(getStatusColor(editFormData.status), 0.1),
            color: getStatusColor(editFormData.status),
            fontWeight: 'medium',
            borderRadius: 1
          }}
        />
      </Box>

      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Bid Details"
          sx={{
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* First row */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editFormData.title}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, title: e.target.value })
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <TitleIcon
                        sx={{ mr: 1, color: theme.palette.primary.main }}
                      />
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contract No"
                  value={editFormData.ContractNo || ''}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      ContractNo: e.target.value
                    })
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <BookmarkIcon
                        sx={{ mr: 1, color: theme.palette.primary.main }}
                      />
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>

              {/* Second row */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    disabled
                    value={editFormData.Type || ''}
                    onChange={(e) => {
                      const selectedType = e.target.value;
                      // Find the TypeID for the selected Type name
                      const typeID = Object.entries(typeMap).find(
                        ([, name]) => name === selectedType
                      )?.[0];

                      setEditFormData({
                        ...editFormData,
                        Type: selectedType,
                        TypeID: typeID ? parseInt(typeID) : editFormData.TypeID
                      });
                    }}
                    startAdornment={
                      <LocalOfferIcon
                        sx={{ ml: 1, mr: 1, color: theme.palette.primary.main }}
                      />
                    }
                    sx={{ borderRadius: 1 }}
                  >
                    {bidTypes.map((type) => (
                      <MenuItem key={type.TypeID} value={type.Type}>
                        {type.Type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    disabled
                    value={editFormData.status}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        status: e.target.value
                      })
                    }
                    startAdornment={
                      <CategoryIcon
                        sx={{ ml: 1, mr: 1, color: theme.palette.primary.main }}
                      />
                    }
                    sx={{ borderRadius: 1 }}
                    renderValue={(selected) => (
                      <Chip
                        label={selected}
                        size="small"
                        sx={{
                          bgcolor: alpha(getStatusColor(selected), 0.1),
                          color: getStatusColor(selected)
                        }}
                      />
                    )}
                  >
                    {statuses.map((status) => (
                      <MenuItem key={status.StatusID} value={status.Status}>
                        <Chip
                          label={status.Status}
                          size="small"
                          sx={{
                            bgcolor: alpha(getStatusColor(status.Status), 0.1),
                            color: getStatusColor(status.Status)
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Third row */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    open={openCategorySelect}
                    onOpen={() => setOpenCategorySelect(true)}
                    onClose={() => setOpenCategorySelect(false)}
                    multiple
                    value={editFormData.category}
                    onChange={(e: SelectChangeEvent<string[]>) =>
                      setEditFormData({
                        ...editFormData,
                        category:
                          typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : e.target.value
                      })
                    }
                    input={
                      <OutlinedInput
                        label="Category"
                        sx={{ borderRadius: 1 }}
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    startAdornment={
                      <CategoryIcon
                        sx={{ ml: 1, mr: 1, color: theme.palette.primary.main }}
                      />
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {categories.map((category) => (
                      <MenuItem
                        key={category.CategoryID}
                        value={category.CategoryName}
                      >
                        <Checkbox
                          checked={
                            editFormData.category.indexOf(
                              category.CategoryName
                            ) > -1
                          }
                        />
                        <ListItemText primary={category.CategoryName} />
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenCategorySelect(false);
                        }}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    disablePast
                    label="Effective Date"
                    value={
                      editFormData.effectiveDate
                        ? new Date(editFormData.effectiveDate)
                        : null
                    }
                    onChange={(date) =>
                      setEditFormData({
                        ...editFormData,
                        effectiveDate: date ? date.toISOString() : undefined
                      })
                    }
                    slots={{
                      openPickerIcon: () => <EventIcon />
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputProps: {
                          sx: { borderRadius: 1 },
                          startAdornment: (
                            <CalendarTodayIcon
                              sx={{ mr: 1, color: theme.palette.primary.main }}
                            />
                          )
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Fourth row */}
              {/* <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Go Live Date"
                    value={
                      editFormData.goLiveDate
                        ? new Date(editFormData.goLiveDate)
                        : null
                    }
                    onChange={(date) =>
                      setEditFormData({
                        ...editFormData,
                        goLiveDate: date ? date.toISOString() : undefined
                      })
                    }
                    slots={{
                      openPickerIcon: () => <EventIcon />
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputProps: {
                          sx: { borderRadius: 1 },
                          startAdornment: (
                            <EventAvailableIcon
                              sx={{ mr: 1, color: theme.palette.primary.main }}
                            />
                          )
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid> */}
              {/* <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Award Date"
                    value={
                      editFormData.awardDate
                        ? new Date(editFormData.awardDate)
                        : null
                    }
                    onChange={(date) =>
                      setEditFormData({
                        ...editFormData,
                        awardDate: date ? date.toISOString() : null
                      })
                    }
                    slots={{
                      openPickerIcon: () => <EventIcon />
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputProps: {
                          sx: { borderRadius: 1 },
                          startAdornment: (
                            <EventIcon
                              sx={{ mr: 1, color: theme.palette.primary.main }}
                            />
                          )
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid> */}

              {/* <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid> */}

              {/* Fifth row */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={editFormData.description}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      description: e.target.value
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <DescriptionIcon
                        sx={{ mt: 1, mr: 1, color: theme.palette.primary.main }}
                      />
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Admin Notes"
                  multiline
                  rows={4}
                  value={editFormData.adminNote}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      adminNote: e.target.value
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <NotesIcon
                        sx={{ mt: 1, mr: 1, color: theme.palette.primary.main }}
                      />
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    mt: 2,
                    pt: 2
                  }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={onCancel}
                    sx={{
                      borderRadius: 1,
                      px: 3
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                    size="large"
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <SaveIcon />
                    }
                    sx={{
                      borderRadius: 1,
                      px: 4,
                      boxShadow: 2
                    }}
                  >
                    {loading ? 'Updating...' : 'Update Bid'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            backdropFilter: 'blur(4px)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="subtitle1">Updating bid...</Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default EditBid;
